import { lazy } from "react";
import { mitWebseiteWeiche } from "@/components/webseite/WebseiteWeiche";
import logoImage from "@/assets/musikonzept-logo.png";
import badgeAppStore from "@/assets/badge-app-store.svg";
import badgeGooglePlay from "@/assets/badge-google-play.png";
import { Mail, Phone, MapPin } from "lucide-react";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";

const Footer = () => {
  const isNativeApp = useIsNativeApp();
  return (
    <footer className="bg-card border-t border-border py-16">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
          {/* Logo & Description */}
          <div className="md:col-span-2 lg:col-span-2 space-y-6">
            <img 
              src={logoImage} 
              alt="MusiKonzept Logo" 
              className="h-14 w-auto"
            />
            <p className="text-muted-foreground max-w-md leading-relaxed">
              Mehr Ideen für mehr Musik mit Kindern. Innovative Online-Plattform für pädagogische Fachkräfte und Kursleiterinnen.
            </p>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <span>MusiKonzept, Deutschland</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                <a href="mailto:info@musikonzept.de" className="hover:text-primary transition-colors">
                  info@musikonzept.de
                </a>
              </div>
            </div>
            {!isNativeApp && (
              <div className="flex items-center gap-3 mt-2">
                <a href="https://apps.apple.com/de/app/musikonzept/id6755689687" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                  <img src={badgeAppStore} alt="Laden im App Store" className="h-[36px] w-auto" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.wnapp.id1762014785410" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                  <img src={badgeGooglePlay} alt="Jetzt bei Google Play" className="h-[36px] w-auto" />
                </a>
              </div>
            )}
          </div>

          {/* Schnellzugriff */}
          <div>
            <h3 className="font-bold text-foreground mb-4 text-lg">Schnellzugriff</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="/kurse" 
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                >
                  Online-Akademie
                </a>
              </li>
              <li>
                <a 
                  href="https://miko.musikonzept.de" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                >
                  Miko Studio
                </a>
              </li>
              <li>
                <a 
                  href="/ausbildung/fachkraft-musik" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Ausbildungen
                </a>
              </li>
              <li>
                <a 
                  href="/termine" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Termine
                </a>
              </li>
              <li>
                <a
                  href="/foerdermoeglichkeiten"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Fördermöglichkeiten
                </a>
              </li>
            </ul>
          </div>

          {/* Preise & Mitgliedschaften - hidden in native app */}
          {!isNativeApp && (
          <div>
            <h3 className="font-bold text-foreground mb-4 text-lg">Preise & Mitgliedschaft</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="/preise" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Preisübersicht
                </a>
              </li>
              {/* Bis 14.10.2026 standen hier Links in die alte Akademie (/cart, 404) und auf einen Anker, den es auf
                  /preise nicht gibt (Sitzung „Aufräumen“). */}
              <li>
                <a 
                  href="/registrieren" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Kostenlos registrieren
                </a>
              </li>
              <li>
                <a 
                  href="/musik-kita" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Musik-Kita-Paket
                </a>
              </li>
              <li>
                <a 
                  href="/login" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Login
                </a>
              </li>
            </ul>
          </div>
          )}

          {/* Hilfe & Support */}
          <div>
            <h3 className="font-bold text-foreground mb-4 text-lg">Hilfe & Support</h3>
            <ul className="space-y-3">
              <li>
                <a href="/kontakt" className="text-muted-foreground hover:text-primary transition-colors">
                  Kontakt
                </a>
              </li>
              <li>
                <a href="/hilfe" className="text-muted-foreground hover:text-primary transition-colors">
                  Hilfe & FAQ
                </a>
              </li>
              <li>
                <a href="/newsletter" className="text-muted-foreground hover:text-primary transition-colors">
                  Newsletter
                </a>
              </li>
              <li>
                <a href="/ueber-uns" className="text-muted-foreground hover:text-primary transition-colors">
                  Über uns
                </a>
              </li>
              <li>
                <a href="/community" className="text-muted-foreground hover:text-primary transition-colors">
                  Community
                </a>
              </li>
              <li>
                <a href="/kinderlieder" className="text-muted-foreground hover:text-primary transition-colors">
                  Kinderlieder
                </a>
              </li>
              {!isNativeApp && (
                <li>
                  <a href="/partner-werden" className="text-muted-foreground hover:text-primary transition-colors">
                    Partner werden
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Rechtliches */}
          <div>
            <h3 className="font-bold text-foreground mb-4 text-lg">Rechtliches</h3>
            <ul className="space-y-3">
              <li>
                <a href="/impressum" className="text-muted-foreground hover:text-primary transition-colors">
                  Impressum
                </a>
              </li>
              <li>
                <a href="/datenschutz" className="text-muted-foreground hover:text-primary transition-colors">
                  Datenschutz
                </a>
              </li>
              <li>
                <a href="/agb" className="text-muted-foreground hover:text-primary transition-colors">
                  AGB
                </a>
              </li>
              <li>
                <a href="/widerruf" className="text-muted-foreground hover:text-primary transition-colors">
                  Widerrufsbelehrung
                </a>
              </li>
              {/* Widerrufsbutton (§ 356a BGB): ohne Anmeldung erreichbar, auf jeder Seite mit Fußzeile (Sitzung
                  „Aufräumen“, 14.10.2026). Gekündigt wird im Konto über „Abo kündigen“ (Daniel, 15.10.2026). */}
              <li className="pt-1">
                <a
                  href="/vertrag-widerrufen"
                  className="inline-flex min-h-[40px] items-center rounded-full border border-[#227277] px-4 text-sm font-semibold text-[#227277] transition-colors hover:bg-[#227277] hover:text-white"
                >
                  Vertrag widerrufen
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} MusiKonzept. Alle Rechte vorbehalten.</p>
            <div className="flex gap-6">
              <a href="https://www.facebook.com/musikonzept" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                Facebook
              </a>
              <a href="https://www.instagram.com/musikonzept" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                Instagram
              </a>
              <a href="https://www.youtube.com/@musikonzept" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                YouTube
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Weiche „Webseite neu“ (Sitzung 9): ohne Weiche genau dieser Fuß, mit Weiche der neue aus src/components/webseite/.
const FooterMitWeiche = mitWebseiteWeiche(Footer, lazy(() => import("@/components/webseite/Fuss")));
export default FooterMitWeiche;
